import React, { useState, useRef, useMemo } from "react";
import { InlineMath } from "react-katex";

export default function PhotoNotebookViewport({
  images = [],
  detectedStepBoxes = [],
  stepDiagnostics = {},
  selectedStep = null,
  onStepSelect = null,
  isAnalyzing = false,
  analysisStatus = "",
  photoProcessingPhase = "",
  showBoxes = true,
  onToggleShowBoxes = null,
  onOpenPhotoModal = null
}) {
  const [localShowBoxes, setLocalShowBoxes] = useState(true);
  const effectiveShowBoxes = onToggleShowBoxes ? showBoxes : localShowBoxes;
  const toggleBoxes = onToggleShowBoxes || (() => setLocalShowBoxes((prev) => !prev));

  const pageRefs = useRef({});

  const totalSteps = useMemo(() => {
    if (!Array.isArray(detectedStepBoxes)) return 0;
    return detectedStepBoxes.length;
  }, [detectedStepBoxes]);

  // Group detected step boxes by pageIndex, clamping safely to image page boundaries
  const boxesByPage = useMemo(() => {
    const map = {};
    const imgCount = Array.isArray(images) ? images.length : 1;
    (Array.isArray(detectedStepBoxes) ? detectedStepBoxes : []).forEach((box) => {
      let p = Number(box?.pageIndex ?? 0);
      if (isNaN(p) || p < 0) p = 0;
      if (imgCount > 0 && p >= imgCount) p = imgCount - 1;
      if (!map[p]) map[p] = [];
      map[p].push(box);
    });
    // Ensure boxes on each page are sorted in strictly increasing stepNo order
    Object.keys(map).forEach((p) => {
      map[p].sort((a, b) => (Number(a?.stepNo) || 0) - (Number(b?.stepNo) || 0));
    });
    return map;
  }, [detectedStepBoxes, images]);

  const scrollToPage = (pageIdx) => {
    const el = pageRefs.current[pageIdx];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (!images || images.length === 0) {
    return (
      <div className="photo-notebook-viewport empty">
        <div className="photo-notebook-empty-msg">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M4 7h3l2-3h6l2 3h3a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" />
            <circle cx="12" cy="14" r="4" />
          </svg>
          <p>No notebook solution pages submitted.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="photo-notebook-viewport">
      {/* Top Controls Bar */}
      <div className="photo-notebook-toolbar">
        <div className="photo-notebook-toolbar-left">
          <span className="photo-toolbar-badge">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 7h3l2-3h6l2 3h3a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" />
              <circle cx="12" cy="14" r="4" />
            </svg>
            <span>
              {images.length} {images.length === 1 ? "Page" : "Pages"}
              {totalSteps > 0 ? ` • ${totalSteps} Steps Identified` : ""}
            </span>
          </span>

          {images.length > 1 && (
            <div className="photo-page-jump-chips">
              {images.map((_, idx) => (
                <button
                  key={`jump_p_${idx}`}
                  type="button"
                  className="photo-jump-chip"
                  onClick={() => scrollToPage(idx)}
                >
                  P{idx + 1}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="photo-notebook-toolbar-right">
          {totalSteps > 0 && (
            <button
              type="button"
              className={`photo-toolbar-toggle-btn ${effectiveShowBoxes ? "active" : ""}`}
              onClick={toggleBoxes}
              title={effectiveShowBoxes ? "Hide step bounding boxes" : "Show step bounding boxes"}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M9 3v18M15 3v18M3 9h18M3 15h18" opacity="0.5" />
              </svg>
              <span>{effectiveShowBoxes ? "Hide Step Boxes" : "Show Step Boxes"}</span>
            </button>
          )}

          {typeof onOpenPhotoModal === "function" && (
            <button
              type="button"
              className="photo-toolbar-action-btn"
              onClick={onOpenPhotoModal}
              title="View attached pages or retake"
            >
              Pages Strip
            </button>
          )}
        </div>
      </div>

      {/* Floating Processing Indicator Banner */}
      {isAnalyzing && (
        <div className="photo-viewport-status-bar">
          <div className="photo-viewport-status-spinner" />
          <div className="photo-viewport-status-text">
            <span className="photo-status-title">
              {photoProcessingPhase === "uploading"
                ? "Saving Notebook Pages..."
                : photoProcessingPhase === "ocr"
                ? "Gemini Flash: Detecting Math Steps & Figures..."
                : "Gemini Pro: Evaluating Step-by-Step Mathematical Equivalence..."}
            </span>
            <span className="photo-status-desc">
              {analysisStatus || "Analyzing your handwritten solution..."}
            </span>
          </div>
        </div>
      )}

      {/* Continuous Scroll Container with ample bottom room */}
      <div className="photo-notebook-scroll-area">
        {images.map((img, pageIdx) => {
          const imgSrc = img?.dataUrl || img?.url || img?.publicUrl || (typeof img === "string" ? img : "");
          const pageBoxes = boxesByPage[pageIdx] || [];

          return (
            <div
              key={`photo_page_${pageIdx}`}
              ref={(el) => (pageRefs.current[pageIdx] = el)}
              className="photo-notebook-page-card"
            >
              <div className="photo-notebook-page-meta">
                <span className="photo-page-number">Page {pageIdx + 1} of {images.length}</span>
                {pageBoxes.length > 0 && (
                  <span className="photo-page-step-count">{pageBoxes.length} {pageBoxes.length === 1 ? "step" : "steps"}</span>
                )}
              </div>

              <div className="photo-notebook-page-body">
                {/* ── LEFT STEP RAIL (Matches Normal Slate) ── */}
                <div className="photo-left-step-rail" aria-label={`Step Rail Page ${pageIdx + 1}`}>
                  {pageBoxes.map((box) => {
                    const rawBox = box?.box2d || [];
                    if (rawBox.length !== 4) return null;
                    const [ymin, , ymax] = rawBox;
                    const centerPct = ((ymin + ymax) / 20).toFixed(2);

                    const diag = stepDiagnostics[box.stepNo] || null;
                    const status = String(diag?.status || "").toLowerCase();
                    const isSelected = Number(selectedStep) === Number(box.stepNo);

                    const isIncorrect = status === "incorrect" || status === "propagated_error";
                    const isIncomplete = status === "incomplete";

                    const shapeClass = isIncorrect
                      ? "shape-triangle"
                      : isIncomplete
                      ? "shape-rhombus"
                      : "shape-circle";

                    const statusClass = status ? `status-${status}` : "status-review";
                    const hasDiag = Boolean(diag && diag.status);

                    return (
                      <div
                        key={`rail_step_${box.stepNo}_${pageIdx}`}
                        className="photo-step-rail-item"
                        style={{ top: `${centerPct}%` }}
                      >
                        <button
                          type="button"
                          className={`slate-step-marker ${
                            hasDiag ? `${shapeClass} ${statusClass}` : "default-step-badge"
                          } ${isSelected ? "is-selected" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (typeof onStepSelect === "function") {
                              onStepSelect(isSelected ? null : box.stepNo);
                            }
                          }}
                          title={diag?.reason || (box.kind === "diagram" ? "Figure" : `Step ${box.stepNo}`)}
                          aria-label={`Step ${box.stepNo}`}
                        >
                          <span className="slate-step-marker-number">{box.stepNo}</span>

                          {/* Step Popover Tooltip */}
                          <span
                            className={`slate-step-popover ${statusClass} ${
                              isSelected ? "is-visible" : ""
                            }`}
                          >
                            <strong className="slate-step-popover-title">
                              Step {box.stepNo} {status ? `• ${status.replace("_", " ").toUpperCase()}` : ""}
                            </strong>
                            {box.latex && (
                              <div className="photo-popover-latex">
                                <InlineMath math={box.latex} />
                              </div>
                            )}
                            <span className="photo-popover-reason">
                              {diag?.reason || (box.kind === "diagram" ? "Geometry diagram detected" : "Recognized handwritten step")}
                            </span>
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* ── NOTEBOOK IMAGE & LIGHT DOTTED BOUNDING BOXES ── */}
                <div className="photo-notebook-image-wrapper">
                  <img
                    src={imgSrc}
                    alt={`Submitted handwritten solution page ${pageIdx + 1}`}
                    className="photo-notebook-image"
                  />

                  {/* Processing Scanline Animation */}
                  {isAnalyzing && !totalSteps && (
                    <div className="photo-scanning-line-overlay" />
                  )}

                  {/* Light Faded / Dotted Step Bounding Layer */}
                  {effectiveShowBoxes && (
                    <div className="photo-step-bounding-layer">
                      {pageBoxes.map((box) => {
                        const rawBox = box?.box2d || [];
                        if (rawBox.length !== 4) return null;
                        const [ymin, xmin, ymax, xmax] = rawBox;

                        const topPct = (ymin / 10).toFixed(2);
                        const leftPct = (xmin / 10).toFixed(2);
                        const widthPct = Math.max(2, (xmax - xmin) / 10).toFixed(2);
                        const heightPct = Math.max(1.5, (ymax - ymin) / 10).toFixed(2);

                        const isSelected = Number(selectedStep) === Number(box.stepNo);
                        const diag = stepDiagnostics[box.stepNo] || null;
                        const status = String(diag?.status || "").toLowerCase();

                        return (
                          <div
                            key={`box_${box.stepNo}_${pageIdx}`}
                            className={`photo-step-bracket ${box.kind === "diagram" ? "is-diagram" : ""} ${
                              isSelected ? "is-selected" : ""
                            } ${status ? `has-${status}` : ""}`}
                            style={{
                              top: `${topPct}%`,
                              left: `${leftPct}%`,
                              width: `${widthPct}%`,
                              height: `${heightPct}%`
                            }}
                            onClick={() => {
                              if (typeof onStepSelect === "function") {
                                onStepSelect(isSelected ? null : box.stepNo);
                              }
                            }}
                            title={`Step ${box.stepNo}${box.latex ? `: ${box.latex}` : ""}`}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
